import { useEffect, useState } from "react";
import api from "../../lib/api";

const B = ({ children }) => <p className="font-sans text-sm text-ink/85 leading-relaxed">{children}</p>;
const Sub = ({ children }) => <p className="font-sans text-sm text-ink/85 leading-relaxed pl-6">{children}</p>;
const H = ({ children }) => <h2 className="font-serif text-2xl text-ink pt-4">{children}</h2>;

function SiteSettings() {
  const [show, setShow] = useState(null);
  const [state, setState] = useState(""); // "" | saving | saved | error

  useEffect(() => {
    api.get("/public/site-settings").then((r) => setShow(r.data?.show_join_form === true)).catch(() => setShow(false));
  }, []);

  const toggle = async (e) => {
    const next = e.target.checked;
    setShow(next);
    setState("saving");
    try {
      const r = await api.put("/admin/site-settings", { show_join_form: next });
      setShow(r.data.show_join_form === true);
      setState("saved");
    } catch {
      setShow(!next);
      setState("error");
    }
  };

  return (
    <div className="paper-card p-5 bg-white" data-testid="site-settings">
      <p className="overline mb-3">Site settings</p>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          role="switch"
          className="mt-1 h-4 w-4 accent-brass"
          checked={show === true}
          disabled={show === null || state === "saving"}
          onChange={toggle}
          data-testid="site-setting-show-join-form"
        />
        <span>
          <span className="block font-sans text-sm text-ink">Show the sign-up form on /join</span>
          <span className="block font-sans text-xs text-stone mt-0.5">Off: /join shows the email-Tanna line. On: the form comes back.</span>
        </span>
      </label>
      <p className="font-mono text-xs mt-3" data-testid="site-setting-status">
        {show === null ? "Loading…" : state === "saving" ? "Saving…" : state === "error" ? (
          <span className="text-garnet">Couldn't save — please try again.</span>
        ) : (
          <span className={state === "saved" ? "text-confirmed" : "text-stone"}>{state === "saved" ? "Saved · " : ""}{show ? "On — the form is showing on /join." : "Off — /join shows the email-Tanna line."}</span>
        )}
      </p>
    </div>
  );
}

export default function ReefHowItWorks() {
  return (
    <div className="space-y-4 max-w-3xl" data-testid="reef-how-it-works">
      <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">HOW THIS SITE WORKS</h1>

      <SiteSettings />

      <H>The public pages</H>
      <B>• workingactor.studio — the Working Actor home page.</B>
      <B>• /join — the class signup page. Flyer and word-of-mouth traffic lands here, and every submission appears in Class Signups.</B>
      <B>• /tvi — the separate TVI page, kept apart from the Whitefire class on purpose.</B>
      <B>• The Rehearsal Room — a free tool for running your sides out loud: it listens and turns the page when you say your line. It opens in a new tab at rehearse.workingactor.studio/blank and is a separate app from this site.</B>
      <B>• /login and /signup — sign-in and account creation. Admins sign in with "Continue with Google".</B>
      <B>• /portal — the student portal.</B>
      <Sub>Dashboard — a call-sheet view of the student's next class, their registration status, assigned sides and any open invoice.</Sub>
      <Sub>My Sides — the scene PDFs assigned to them, to read in the browser or download.</Sub>
      <Sub>Classes — the class schedule, where they can request a seat (nothing auto-approves; the studio decides).</Sub>
      <Sub>Requests — where they send the studio a question or a submission and follow its status.</Sub>
      <Sub>Billing — their invoices, each with its amount, open-or-paid status and a PDF copy.</Sub>
      <Sub>Profile — their account details and contact info, which they can update.</Sub>

      <H>The Reef (this admin area)</H>
      <B>• The Reef — the front page: counts of pending seat requests, open questions and submissions, and unpaid invoices, plus the upcoming classes.</B>
      <B>• Registrations — approve, decline or waitlist each seat request.</B>
      <B>• Class Signups — every submission from the /join page, with mark-handled and archive.</B>
      <B>• Requests — student questions and submissions, with resolve and approve/decline actions.</B>
      <B>• Classes — create and edit class sessions (date, time, venue, capacity), manage each class's roster and print its sign-in sheet.</B>
      <B>• Sides — upload scene PDFs and assign them to students.</B>
      <B>• Students — the roster: add a student and view their record.</B>
      <B>• Billing — every invoice: send it, send a reminder, or mark it paid.</B>
      <B>• API access — personal API keys, the API reference, and recent activity.</B>
      <B>• How this site works — this page.</B>

      <H>Who the admins are</H>
      <B>Keaton Simons (site owner), Eliza Roberts, Tanna Frederick and Sean Paul Payton. Keaton, Tanna and Sean sign in with "Continue with Google" using their own Gmail addresses.</B>

      <H>API access</H>
      <B>Any admin can create a personal API key under API access. A key lets your own software or assistant work with the studio's data, with the same rights you have as an admin. You see the key once, when you create it, so keep it somewhere safe, like a password manager. You can revoke it anytime, and a revoked key stops working immediately. Keys can't create or manage other keys; that always takes a real sign-in. Actions taken with a key are recorded under Recent activity.</B>

      <H>Changing the site</H>
      <B>Keaton Simons' team builds and publishes the site's pages and features. To change something, email the request to keatonhillsimons@gmail.com. Small changes usually go live the same day.</B>

      <H>Privacy</H>
      <B>Class Signups, Students and Billing hold students' personal information. Keep it inside the studio: don't share exports or screenshots publicly, and delete exported files when you're done with them.</B>
    </div>
  );
}
